import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import {
  AgentWorkService,
  WorkDecision,
  WorkEmailPreview,
  WorkRunView,
  hasPendingSkill,
  isHitlPending,
  pendingEmail,
  pendingSkillCallIds,
  publicWorkOutcome,
} from '../../../../core/services/agent-work.service';
import { LayoutUtilsService, MessageType } from '../../../../core/_base/crud';

@Component({
  selector: 'kt-task-agent-hitl',
  templateUrl: './task-agent-hitl.component.html',
  styleUrls: ['./task-agent-hitl.component.scss'],
})
export class TaskAgentHitlComponent implements OnChanges {
  @Input() taskId: number;
  @Input() runId: string;
  @Output() refreshTask = new EventEmitter<void>();
  loading = false;
  deciding = false;
  error = '';
  run: WorkRunView = null;
  reason = '';

  constructor(
    private agentWorkService: AgentWorkService,
    private layoutUtils: LayoutUtilsService,
  ) {}

  get outcome(): string {
    return publicWorkOutcome(this.run);
  }

  get statusLabel(): string {
    if (this.loading) {
      return 'Loading';
    }
    if (this.outcome) {
      return this.outcome;
    }
    if (!this.runId) {
      return 'Not started';
    }
    return 'Unknown';
  }

  get pending(): boolean {
    return isHitlPending(this.run);
  }

  get canDecide(): boolean {
    return (
      this.outcome === 'HumanInputRequired' &&
      pendingSkillCallIds(this.run).length > 0 &&
      !this.deciding
    );
  }

  get email(): WorkEmailPreview | null {
    return pendingEmail(this.run);
  }

  get canApprove(): boolean {
    return this.canDecide && (!this.isEmailSkill || !!this.email);
  }

  get skillName(): string {
    const pending = this.run?.pending_approval;
    return pending?.skill_name || pending?.skill_calls?.[0]?.skill_name || '';
  }

  get isEmailSkill(): boolean {
    return hasPendingSkill(this.run, 'Messaging.SendEngage');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.runId || changes.taskId) {
      this.reason = '';
      this.load();
    }
  }

  refresh(): void {
    this.load();
    this.refreshTask.emit();
  }

  decide(decision: WorkDecision): void {
    const runId = this.runId;
    const skillCallIds = pendingSkillCallIds(this.run);
    if (
      !runId ||
      skillCallIds.length === 0 ||
      this.deciding ||
      (decision === 'approve' && !this.canApprove) ||
      (decision === 'deny' && !this.canDecide)
    ) {
      return;
    }
    this.deciding = true;
    this.error = '';
    this.agentWorkService.submitDecision(runId, skillCallIds, decision).subscribe(
      (run) => {
        this.deciding = false;
        if (runId !== this.runId) {
          return;
        }
        this.run = run;
        this.layoutUtils.showActionNotification(
          decision === 'approve' ? 'Skill approved' : 'Skill denied',
          MessageType.Update,
          3000,
          false,
          false,
        );
      },
      (error: HttpErrorResponse) => {
        this.deciding = false;
        if (runId !== this.runId) {
          return;
        }
        this.error =
          error?.status === 403
            ? 'You do not have permission to decide this Agent Run.'
            : 'Unable to submit the decision.';
      },
    );
  }

  private load(): void {
    const runId = this.runId;
    this.run = null;
    this.error = '';
    this.loading = !!runId;
    if (!runId) {
      return;
    }
    this.agentWorkService.getRun(runId).subscribe(
      (run) => {
        if (runId !== this.runId) {
          return;
        }
        this.loading = false;
        this.run = run;
      },
      (error: HttpErrorResponse) => {
        if (runId !== this.runId) {
          return;
        }
        this.loading = false;
        if (error?.status === 404) {
          this.error = '';
          this.run = null;
          return;
        }
        this.error =
          error?.status === 403
            ? 'You do not have permission to view this Agent Run.'
            : 'Unable to load Agent Run status.';
      },
    );
  }
}
